import type { RunOperation } from "@/api/runQueue";
export interface RunRider {
  heat_rider_id: string;
  rider_id?: string;
  name: string;
  bib: string | null;
  current_attempt: number;
  repair_until?: string | null;
  next_attempt_number?: number | null;
  previous_attempt_number?: number;
  version?: number;
  status: string;
  status_label?: string | null;
  attempts: {
    attempt_number: number;
    counted?: boolean;
    attempt_type?: string;
    slot?: number;
    reride_of_attempt?: number | null;
    reride?: { original_attempt_number: number; original_own_score: string | null };
    version?: number;
    released?: boolean;
    status: string;
    judge_scores?: Record<string, string | number>;
    applied_modifiers?: { id: string; name: string }[];
  }[];
}
export interface RunHeat {
  id: string;
  name: string;
  division_id: string | null;
  division: string | null;
  round_id?: string | null;
  sort_order?: number;
  scheduled_at?: string | null;
  area_id: string | null;
  area: string | null;
  status: string;
  version: number;
  locked?: boolean;
  max_attempts: number;
  attempt_slots?: { attempt_type: string; slot: number }[];
  timer_duration_s?: number | null;
  format?: {
    kind?: string;
    jam?: { mode: string; best_n?: number };
    passes_per_attempt?: number;
    type?: string;
    out_of_course_window?: string | null;
    fall_past_marker_ends_run?: string;
    bonus_element?: string;
  };

  placement_pending?: boolean;
  tie_pending?: boolean;
  end_requires_choice?: boolean;
  placement_missing?: number[];
  riders: RunRider[];
  focus_heat_rider_id: string | null;
  focus_source: string | null;
  judge_count: number;
  panel_missing: { seat_id: string; judge_number: number }[];
  judging: {
    force_end_default?: "count" | "void";
    judge_placements?: boolean;
    head_judge_gate?: boolean;
    judges_can_edit_live?: boolean;
    anchor_mode?: string;
    modifiers?: boolean;
    re_rides?: {
      enabled?: boolean;
      reride_reasons?: string[] | null;
      reride_selection?: string;
      allow_resume?: boolean;
    };
  };
  timer: { remaining_s: number; running: boolean; client_at?: number } | null;
}
export interface RunState {
  server_time?: string;
  /** Local receipt time is cached with its server clock sample for offline reload. */
  clock_received_at?: number;
  competition_id: string;
  name: string;
  roles: string[];
  principal: string;
  account: boolean;
  slug?: string;
  status?: string;
  seat?: number | null;
  features: Record<string, boolean>;
  version?: number;
  capabilities?: Record<string, boolean>;
  rounds?: {
    id: string;
    division_id: string;
    name: string;
    status: string;
    version: number;
    stage: number;
    stage_order: number;
    sort_order: number;
  }[];
  schedule?: import("@/components/schedule/types").ScheduleProjection | null;
  heats: RunHeat[];
  areas: { id: string; name: string }[];
  modifiers?: {
    id: string;
    name: string;
    applicable_by: string[];
    sets_status?: string | null;
    preset_key?: string | null;
  }[];
}
/** Project registry navigation only; results remain server-owned. */
export function projectAttemptMove(
  heat: Pick<RunHeat, "attempt_slots">,
  rider: RunRider,
  advancing: boolean,
  restore?: number,
): void {
  const slots = heat.attempt_slots;
  const rows = rider.attempts;
  const inactive = new Set(["superseded", "reverted", "reride_pending"]);
  const plan = () => {
    let maximum = Math.max(0, ...rows.map((r) => r.attempt_number));
    return slots?.map((slot) => {
      const matches = rows
        .filter((r) => r.attempt_type === slot.attempt_type && r.slot === slot.slot)
        .sort((a, b) => b.attempt_number - a.attempt_number);
      const row =
        matches.find((r) => !inactive.has(r.status)) ??
        matches.find((r) => r.status === "reverted" && !r.reride_of_attempt);
      return { ...slot, number: row?.attempt_number ?? ++maximum };
    });
  };
  const current = rows.find((r) => r.attempt_number === rider.current_attempt);
  const order = plan();
  const index =
    slots?.findIndex((s) => s.attempt_type === current?.attempt_type && s.slot === current?.slot) ??
    -1;
  const previous = () => {
    if (!slots || !order) return rider.previous_attempt_number;
    for (let i = index - 1; i >= 0; i--) {
      const candidate = rows
        .filter(
          (r) =>
            r.attempt_type === slots[i].attempt_type &&
            r.slot === slots[i].slot &&
            !inactive.has(r.status),
        )
        .sort((a, b) => b.attempt_number - a.attempt_number)[0];
      if (candidate) return candidate.attempt_number;
    }
    return 0;
  };
  const target =
    restore ?? (advancing ? (order?.[index + 1]?.number ?? rider.next_attempt_number) : previous());
  if (target == null) return;
  if (restore === undefined && !advancing && current) current.status = "reverted";
  let selected = rows.find((r) => r.attempt_number === target);
  if (target > 0 && !selected) {
    const entry = order?.find((s) => s.number === target);
    if (entry) {
      selected = {
        attempt_number: target,
        attempt_type: entry.attempt_type,
        slot: entry.slot,
        status: "open",
      };
      rows.push(selected);
    }
  }
  if (selected?.status === "reverted") selected.status = "open";
  rider.current_attempt = target;
  const nextPlan = plan();
  const selectedIndex =
    slots?.findIndex(
      (s) => s.attempt_type === selected?.attempt_type && s.slot === selected?.slot,
    ) ?? -1;
  rider.next_attempt_number = nextPlan
    ? (nextPlan[selectedIndex + 1]?.number ?? null)
    : advancing
      ? null
      : (current?.attempt_number ?? null);
  if (slots) {
    rider.previous_attempt_number = 0;
    for (let i = selectedIndex - 1; i >= 0; i--) {
      const prior = rows
        .filter(
          (r) =>
            r.attempt_type === slots[i].attempt_type &&
            r.slot === slots[i].slot &&
            !inactive.has(r.status),
        )
        .sort((a, b) => b.attempt_number - a.attempt_number)[0];
      if (prior) {
        rider.previous_attempt_number = prior.attempt_number;
        break;
      }
    }
  }
}

/** Local intent only. Never computes results or confirms public delivery. */
export function projectRunState(server: RunState, ops: RunOperation[], now = Date.now()): RunState {
  const advanceClock = (heat: RunHeat, at: number) => {
    if (heat.timer?.client_at) {
      if (heat.timer.running)
        heat.timer.remaining_s -= Math.max(0, (at - heat.timer.client_at) / 1000);
      heat.timer.client_at = Math.max(at, heat.timer.client_at);
    }
  };
  const state = structuredClone(server);
  for (const op of ops) {
    const heat = state.heats.find((h) => h.id === op.heat_id);
    if (!heat) continue;
    const at = Date.parse(op.client_submitted_at);
    advanceClock(heat, at);
    const rider = heat.riders.find((r) => r.heat_rider_id === op.payload.heat_rider_id);
    if (op.op === "start" && heat.status === "pending") {
      heat.status = "live";
      heat.version += 1;
      heat.end_requires_choice = heat.riders.length > 0;
      if (state.features.timer && heat.timer_duration_s)
        heat.timer = { remaining_s: heat.timer_duration_s, running: true, client_at: at };
      heat.riders.forEach((r) => {
        if (r.current_attempt <= 0) projectAttemptMove(heat, r, true);
      });
    }
    if (op.op === "jam-append") heat.version += 1;
    if (op.op === "end") {
      heat.status = "complete";
      if (heat.timer) heat.timer.running = false;
    }
    if (op.op === "abandon") heat.status = "abandoned";
    if (op.op === "focus") {
      heat.focus_heat_rider_id = op.payload.heat_rider_id ? String(op.payload.heat_rider_id) : null;
      heat.focus_source = "starter";
    }
    if (["attempt-advance", "attempt-revert"].includes(op.op) && rider) {
      projectAttemptMove(
        heat,
        rider,
        op.op === "attempt-advance",
        typeof op.payload.restore_current_attempt === "number"
          ? op.payload.restore_current_attempt
          : undefined,
      );
      heat.end_requires_choice = true;
    }
    if (op.op === "status" && rider) {
      rider.status = String(op.payload.status);
      rider.version = (rider.version ?? 0) + 1;
    }
    if (op.op === "fall" && rider)
      rider.status_label = op.payload.undo_client_id ? null : "Fall queued";
    if (op.op === "pause" && heat.timer) heat.timer.running = false;
    if (op.op === "resume" && heat.timer) heat.timer.running = true;
    if (op.op === "adjust" && heat.timer) heat.timer.remaining_s += Number(op.payload.delta_s ?? 0);
  }
  state.heats.forEach((heat) => advanceClock(heat, now));
  return state;
}

/** Operational labels retain the original identity without exposing any score. */
export function rerideLabel(
  attempt:
    | {
        status?: string;
        reride_of_attempt?: number | null;
        reride?: { original_attempt_number: number };
      }
    | undefined,
): string | null {
  const original = attempt?.reride?.original_attempt_number ?? attempt?.reride_of_attempt;
  return original
    ? `RR of attempt ${original}`
    : attempt?.status === "reride_pending"
      ? "Original · re-ride pending"
      : null;
}
